import { useEffect, useState } from 'react';
import { PaperPlaneRight, X, Rocket, CaretDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { talkToSahibAPI, chatgptAdsAPI, vaultAPI } from '@/lib/api';

// Phase 0 (P0.9): credentials are never entered in Talk to Martian. Every
// "connect" affordance here routes to Connections instead.
export const CONNECTIONS_PATH = '/growth/data-connections';
const goToConnections = () => { window.location.assign(CONNECTIONS_PATH); };
import { toast } from 'sonner';
import { GLIMState, resolveProductState } from '@/brand';
import { useSharedState } from '@/hooks/useSharedConversationState';
import { EVIDENCE_LABELS, formatEvidenceValue } from '@/lib/evidenceFormatting';

// Milestone 36 (Talk to Martian: Intelligence-First Chat UX), Section 10 --
// each honest-limitation `kind` (talk_to_martian.py's own
// `_domain_structured_fields`/`_recommendation_structured_fields`) gets
// its own real label, never a single generic "I don't know." (Section
// 10's own explicit distinction). Purely a display label -- the actual
// decision about WHICH kind applies is made entirely server-side, from
// real service state, never guessed here.
// Milestone 42 (AI CGO Workspace) -- exported so SahibBriefingPage.js's
// own "What Martian Found" section can label a domain's honest limitation
// with the exact SAME four-way vocabulary, rather than a second,
// slightly-different copy that could drift from this one.
export const LIMITATION_KIND_LABEL = {
  unsupported: 'Not production-grade yet',
  capability_unavailable: 'Not connected yet',
  data_unavailable: "Couldn't retrieve this",
  insufficient_data: 'Not enough data yet',
};

// Milestone 30 (Real ChatGPT Ads Connection + Execution Verification) --
// a real, live-reproduced bug this milestone's own audit found: every
// "not connected" reply this panel can receive (chat text AND the
// plan/change-plan button handlers) was already being generated
// honestly by the backend, but the ONLY place that ever looked for it
// (each handler's own `catch` block) was dead code in the one path that
// actually matters -- CampaignPlanService/CampaignChangePlanService's
// own approve_and_execute (chatgpt_ads_execution.py) deliberately catches
// a missing-credential failure INTERNALLY and returns a normal 200
// response with status "execution_failed" (a real persisted plan state,
// never an HTTP error) so the failure is a real, inspectable record --
// meaning the credential-missing case never actually reaches a `catch`
// block at all in real use. Detected here ONCE, reused everywhere a
// reply might mention it (the plain chat path, the button handlers'
// success branch, and their catch blocks, kept as a defensive fallback
// for a genuine network/HTTP-level failure). Matches BOTH real phrasings
// the backend can produce for the same underlying condition
// (chatgpt_ads_execution.py's own "...execution credential..." and
// connectors/chatgpt_ads.py's own "...credential is connected...").
function mentionsMissingChatGPTAdsCredential(text) {
  return typeof text === 'string' && /no chatgpt ads(?: execution)? credential/i.test(text);
}

// Milestone 12 (AI CGO Workspace + Talk to Martian Architectural Primitive)
// -- the real context-aware shell for Martian's future conversational
// surface.
//
// Milestone 23 (ChatGPT Ads -- AI CGO Execution Loop) -- the FIRST real
// backend call this panel ever makes. Every OTHER kind of message still
// gets the exact same honest "not connected yet" reply Milestone 12
// shipped (talk_to_martian.py's own deterministic gate falls through to it
// unchanged) -- this is not a general chat backend, only one narrow,
// human-approved campaign-planning path (see talk_to_martian.py's module
// docstring for the full safety architecture: free text can plan, never
// execute; only an explicit approval on an already-displayed plan can).
//
// Context contract (the minimal, explicit shape SahibBriefingPage passes
// down -- see its own askMartianAbout()):
//   { clientId, surface, entityType, entityId, entityLabel, workflowState }
// `clientId` (this component's own prop, always data.client.id) is the
// real WORKSPACE id, never derived from the authenticated user -- the
// same separation of identity vs. context this milestone's own message
// route requires (server.py resolves tenant_id from the authenticated
// user, client_id from this prop).
// Milestone 28 -- small, pure formatters shared by the change-plan card
// and its executed-reply text below, mirroring talk_to_martian.py's own
// _money()/_build_change_executed_reply() exactly (this button-driven
// path is a separate call from the chat message route, the same way
// Milestone 23's own handleApprove below already builds its own
// "Launched..." text client-side rather than round-tripping through
// /talk-to-sahib/.../message).
function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return 'an unknown amount';
  const text = `${amount}`;
  return currency ? `${currency} ${text}` : text;
}

function describeChangeExecuted(changePlan) {
  const receipt = changePlan.execution_receipt || {};
  const timestamp = receipt.execution_timestamp || '';
  if (changePlan.action === 'pause') {
    return (
      `Paused. The platform now reports this campaign as Paused (as of ${timestamp}). ` +
      "Note: per OpenAI's own advertising terms, ads may keep delivering (and remain billable) for up to 24 hours after a campaign is paused."
    );
  }
  if (changePlan.action === 'resume') {
    return `Resumed. The platform now reports this campaign as Active (as of ${timestamp}).`;
  }
  if (changePlan.action === 'update_budget') {
    const returned = receipt.returned_status || {};
    const currency = changePlan.current_state?.currency_code;
    return `Done. The platform now reports the lifetime budget as ${formatMoney(returned.lifetime_budget_amount, currency)}.`;
  }
  return 'Done.';
}

export function TalkToMartianPanel({ clientId, context, onClearContext }) {
  const [draft, setDraft] = useState('');
  // Milestone 36 (Section 12) -- real bug M34's audit named: DashboardLayout.js
  // mounts this component TWICE for the same real conversation (a desktop
  // <aside> instance and a mobile <Drawer> instance -- see that file's own
  // comments), and every state variable below used to be a plain local
  // `useState`, so the two instances silently ran two independent
  // conversations. `useSharedState(clientId, ...)` is a `useState`-shaped
  // drop-in (see hooks/useSharedConversationState.js) backed by one shared
  // value per real clientId, so both instances now read/write the exact
  // SAME thread/plan/change_plan/connection-status -- opening the drawer,
  // or resizing past the breakpoint, no longer starts over. This is
  // identification only (Section 12's own explicit rule): every backend
  // call below still passes its own explicit clientId/tenant_id, verified
  // server-side exactly as before.
  const [thread, setThread] = useSharedState(clientId, 'thread', []); // [{ role: 'user' | 'system', text, ... }]
  // A client-side correlation id only, unrelated to authentication --
  // scopes approval matching to one conversation (Section 6), nothing more.
  const [threadId] = useSharedState(clientId, 'threadId', () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `thread-${Date.now()}-${performance.now().toString(36).replace('.', '')}`));
  const [sending, setSending] = useState(false);
  const [plan, setPlan] = useSharedState(clientId, 'plan', null);
  const [actingOnPlan, setActingOnPlan] = useState(false);
  // Milestone 28 -- the SAME real CampaignChangePlan object
  // talk_to_martian.py's "approve"/"cancel" chat replies already act on
  // (pause/resume/update_budget, and recommendations that produce one) --
  // this is only a second, button-driven way to reach the exact same
  // chatgpt_ads_change_plan_svc.approve_and_execute/cancel, never a
  // second approval system.
  const [changePlan, setChangePlan] = useSharedState(clientId, 'changePlan', null);
  const [actingOnChangePlan, setActingOnChangePlan] = useState(false);
  // Milestone 36 -- which message indices have their evidence disclosure
  // open. Deliberately local per mounted instance (unlike the
  // conversation content above): the desktop and mobile surfaces are
  // never visible at the same time (one is a hidden lg:block aside, the
  // other a drawer overlay), so there is no real "two open panels
  // disagreeing" case to fix here, only the conversation content itself.
  const [expandedEvidence, setExpandedEvidence] = useState({});
  // Milestone 30 -- a real, already-existing, tenant/client-scoped check
  // (GET /vault/keys, the SAME route DataConnectionsPage.js already uses
  // for every other provider) -- never a new credentials framework, never
  // a second connection mechanism. This component only ever learns a
  // safe boolean from it (a key_name present or not); the real secret
  // value is never requested (`/vault/keys` itself never returns it) and
  // never stored in this component's state. null = not checked yet
  // (never rendered as a false "not connected").
  const [chatgptAdsConnected, setChatgptAdsConnected] = useSharedState(clientId, 'chatgptAdsConnected', null);

  // Milestone 36: setChatgptAdsConnected now comes from useSharedState
  // instead of useState directly (still stable across renders for a
  // given clientId, via the same useCallback pattern a plain useState
  // setter already gets from React) -- the linter can no longer verify
  // that stability on its own for a custom hook's return value.
  // Re-running this effect on every identity change of the setter would
  // be redundant, never harmful; deliberately NOT adding it to the deps
  // array below.
  //
  // Day 2.1 (2026-09-23): the eslint-disable directive itself used to
  // sit here, one comment block above where the exhaustive-deps warning
  // is actually reported (the dependency array on the closing line
  // below) -- `eslint-disable-next-line` only ever silences the single
  // physical line right after it, which was another comment line, so it
  // silenced nothing and the warning fired anyway (fails a strict
  // `CI=true` build). Moved to the line it needs to be on; no other
  // change -- the effect's own behavior, and the reasoning above for
  // deliberately excluding this setter, are unchanged.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    vaultAPI.keys(clientId)
      .then((res) => {
        if (cancelled) return;
        const keys = res.data || [];
        setChatgptAdsConnected(keys.some((k) => k.key_name === 'chatgpt_ads_execution'));
      })
      .catch(() => { if (!cancelled) setChatgptAdsConnected(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Milestone 43 -- root-cause fix for the real, live-reproduced bug M42's
  // own live validation found: `clientId` was NEVER actually missing (a
  // real request always reached the real backend route) -- the generic
  // suggested-prompt text itself ("Explain this"/"Review the
  // recommendation"/"What should I check?") never matched ANY of
  // talk_to_martian.py's real deterministic routing patterns, so the
  // backend correctly and honestly fell through to its own generic
  // unclassified-message reply (FALLBACK_MESSAGE_WITH_CONTEXT_TEMPLATE)
  // -- which happens to be worded almost identically to this panel's own
  // local `!clientId` copy below, making the two failure modes look the
  // same on screen even though only one of them is a real bug.
  //
  // The fix keeps the exact same three-button affordance but sends REAL
  // text this file's own backend counterpart already recognizes:
  // domain-specific real opening questions (verified against talk_to_
  // martian.py's own _AI_SEARCH_PATTERN/_COMMERCE_PATTERN/_SOCIAL_PATTERN/
  // _CREATIVE_PATTERN/_CREATOR_PATTERN/_BRAND_GROWTH_PATTERN/
  // _SINGLE_CHANNEL_OTHER_PATTERN, one per real domain key the M42
  // domain-findings bridge already returns) for a `finding` context,
  // _PREPARED_WORK_PATTERN's own real phrasing for a `prepared_work`
  // context, and the bare, universally-real "Why?"/"What would you do?"
  // for everything else -- never a second routing system, only real
  // strings the EXISTING gates already match.
  const DOMAIN_OPENING_QUESTION = {
    ai_search: 'How is my AI visibility doing?',
    commerce: 'How is my store doing?',
    social: 'How is my social doing?',
    meta: 'How is Meta doing?',
    google: 'How is Google doing?',
    creative: "What's my creative strategy?",
    creator: 'How is my creator pipeline doing?',
    brand_growth: 'How is my brand growth doing?',
  };
  const domainOpeningQuestion = context?.domain && DOMAIN_OPENING_QUESTION[context.domain];
  const examplePrompts = context?.entityType === 'prepared_work'
    ? ["What's prepared for me?", 'Why?']
    : domainOpeningQuestion
      ? [domainOpeningQuestion, 'Why?', 'What would you do?']
      : context
        ? ['Why?', 'What would you do?']
        : ['What needs my attention right now?', "What's changed recently?"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setThread((prev) => [...prev, { role: 'user', text }]);
    setDraft('');

    // Real context, but no real conversation backend exists for
    // workspace-less surfaces (e.g. before a client record loads yet) --
    // the exact same honest fallback as before, never a fabricated answer.
    if (!clientId) {
      setThread((prev) => [...prev, {
        role: 'system',
        text: context
          ? `Talk to Martian isn't connected to a live conversation yet — this question has been noted against ${context.entityLabel}, and will be answered here once it is.`
          : "Talk to Martian isn't connected to a live conversation yet — your question has been noted, and will be answered here once it is.",
      }]);
      return;
    }

    setSending(true);
    try {
      const res = await talkToSahibAPI.sendMessage(clientId, threadId, text, context || null);
      // Milestone 36 (Section 2/16) -- the reply's structured fields
      // (state/domain/judgment/evidence/recommendation/prepared_work/
      // limitation), when present, ride along on the SAME message object
      // as `text` -- never a second round-trip, never a second source of
      // truth. Every one of these is optional (a plain ChatGPT Ads reply
      // carries none of them and renders exactly as before M36).
      setThread((prev) => [...prev, {
        role: 'system', text: res.data.reply, connectCta: mentionsMissingChatGPTAdsCredential(res.data.reply),
        state: res.data.state, domain: res.data.domain, judgment: res.data.judgment,
        evidence: res.data.evidence, recommendation: res.data.recommendation,
        preparedWork: res.data.prepared_work, limitation: res.data.limitation,
      }]);
      if (res.data.plan) setPlan(res.data.plan);
      // Milestone 28 -- was a real, silently-dropped gap before this
      // milestone: every management/recommendation reply already
      // returned a real change_plan, but nothing here ever looked at it.
      if (res.data.change_plan) setChangePlan(res.data.change_plan);
    } catch (err) {
      setThread((prev) => [...prev, { role: 'system', text: "Martian couldn't process that just now — please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async () => {
    if (!plan || actingOnPlan) return;
    setActingOnPlan(true);
    setThread((prev) => [...prev, { role: 'system', text: 'Launching…' }]);
    try {
      const res = await chatgptAdsAPI.approvePlan(clientId, plan.id);
      setPlan(res.data);
      if (res.data.status === 'executed') {
        setThread((prev) => [...prev, { role: 'system', text: `Launched. Real campaign id: ${res.data.execution_receipt.campaign_id}.` }]);
      } else {
        // Milestone 30 -- THE real bug: approve_and_execute
        // (chatgpt_ads_execution.py) catches a missing-credential failure
        // internally and returns this exact 200 response (status
        // "execution_failed", a real persisted plan state) -- this branch,
        // not the catch below, is what a real missing-credential launch
        // attempt actually hits. A small inline CTA (never an intrusive
        // auto-opened modal) lets the user connect right from this message.
        const text = `Martian couldn't launch this campaign: ${res.data.execution_error || 'unknown error'}`;
        setThread((prev) => [...prev, { role: 'system', text, connectCta: mentionsMissingChatGPTAdsCredential(text) }]);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const text = `Martian couldn't launch this campaign: ${detail || 'please try again'}`;
      setThread((prev) => [...prev, { role: 'system', text, connectCta: mentionsMissingChatGPTAdsCredential(text) }]);
    } finally {
      setActingOnPlan(false);
    }
  };

  const handleCancelPlan = async () => {
    if (!plan || actingOnPlan) return;
    setActingOnPlan(true);
    try {
      const res = await chatgptAdsAPI.cancelPlan(clientId, plan.id);
      setPlan(res.data);
      setThread((prev) => [...prev, { role: 'system', text: "Okay, I won't launch that campaign." }]);
    } catch (err) {
      toast.error('Could not cancel the plan right now.');
    } finally {
      setActingOnPlan(false);
    }
  };

  const handleApproveChange = async () => {
    if (!changePlan || actingOnChangePlan) return;
    setActingOnChangePlan(true);
    setThread((prev) => [...prev, { role: 'system', text: 'Working on it…' }]);
    try {
      const res = await chatgptAdsAPI.approveChangePlan(clientId, changePlan.id);
      setChangePlan(res.data);
      if (res.data.status === 'executed') {
        setThread((prev) => [...prev, { role: 'system', text: describeChangeExecuted(res.data) }]);
      } else {
        // Milestone 30 -- the same real bug as handleApprove above:
        // CampaignChangePlanService.approve_and_execute also catches a
        // missing-credential failure internally (never raises), so this
        // branch is the one that actually needs the CTA, not the catch.
        const text = `I couldn't change the campaign: ${res.data.execution_error || 'unknown error'}`;
        setThread((prev) => [...prev, { role: 'system', text, connectCta: mentionsMissingChatGPTAdsCredential(text) }]);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const text = `I couldn't change the campaign: ${detail || 'please try again'}`;
      setThread((prev) => [...prev, { role: 'system', text, connectCta: mentionsMissingChatGPTAdsCredential(text) }]);
    } finally {
      setActingOnChangePlan(false);
    }
  };

  const handleCancelChange = async () => {
    if (!changePlan || actingOnChangePlan) return;
    setActingOnChangePlan(true);
    try {
      const res = await chatgptAdsAPI.cancelChangePlan(clientId, changePlan.id);
      setChangePlan(res.data);
      setThread((prev) => [...prev, { role: 'system', text: "Okay, I won't make that change." }]);
    } catch (err) {
      toast.error('Could not cancel that change right now.');
    } finally {
      setActingOnChangePlan(false);
    }
  };

  const planIsReady = plan && plan.status === 'ready_for_approval';
  const planIsTerminal = plan && ['executed', 'execution_failed', 'cancelled'].includes(plan.status);
  const changePlanIsReady = changePlan && changePlan.status === 'ready_for_approval';
  const changePlanIsTerminal = changePlan && ['executed', 'execution_failed', 'cancelled'].includes(changePlan.status);

  // Visual System v1 -- Talk to Martian is GLIM-only. The face mirrors what
  // the panel is REALLY doing (Product State Engine), never a decorative loop:
  // sending -> thinking, evidence shown -> analyzing, a recommendation ->
  // recommending, a plan awaiting the human -> approval required, an approved
  // plan being launched -> acting, executed -> success, failed -> error.
  const lastReply = [...thread].reverse().find((m) => m.role === 'system');
  const talkGlimState = resolveProductState({
    error: plan?.status === 'execution_failed' || changePlan?.status === 'execution_failed',
    executing: actingOnPlan || actingOnChangePlan,
    approvalRequired: !!(planIsReady || changePlanIsReady),
    loading: sending,
    analyzing: !!lastReply?.evidence?.items?.length,
    recommending: !!lastReply?.recommendation,
    completed: plan?.status === 'executed' || changePlan?.status === 'executed',
  });

  return (
    <div
      className="flex flex-col h-full max-h-[calc(100vh-3rem)] bg-[var(--martian-surface,var(--surface))] p-4"
      data-testid="talk-to-martian-panel"
    >
      <div className="flex items-center gap-2 pb-3 mb-3 border-b-2 border-[var(--martian-rule-strong,var(--border-light))]">
        <GLIMState state={talkGlimState} size={44} showLabel={false} className="shrink-0" />
        <div>
          <p className="text-[13px] font-semibold text-[var(--martian-ink,var(--text-primary))] leading-none">Martian</p>
          <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))] mt-0.5">AI CGO</p>
        </div>
      </div>

      {context ? (
        <div className="pb-3 mb-1 border-b border-[var(--martian-rule,var(--border-light))]" data-testid="talk-to-martian-context">
          <p className="text-[10px] text-[var(--martian-ink-muted,var(--text-tertiary))] mb-1">I found something worth looking at</p>
          <div className="flex items-start justify-between gap-2">
            <p className="text-[12.5px] font-medium text-[var(--martian-ink,var(--text-primary))] leading-snug">{context.entityLabel}</p>
            <button
              onClick={onClearContext}
              data-testid="talk-to-martian-clear-context"
              aria-label="Clear context"
              className="text-[var(--martian-ink-muted,var(--text-tertiary))] hover:text-[var(--martian-ink,var(--text-primary))] shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-[var(--martian-ink-muted,var(--text-secondary))] leading-relaxed pb-3 mb-1 border-b border-[var(--martian-rule,var(--border-light))]" data-testid="talk-to-martian-empty">
          What would you like to understand?
        </p>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto py-3 space-y-2.5" data-testid="talk-to-martian-thread">
        {thread.length === 0 ? (
          <div className="space-y-1.5">
            {examplePrompts.map((p) => (
              <button
                key={p}
                onClick={() => setDraft(p)}
                data-testid="talk-to-martian-example-prompt"
                className="block w-full text-left text-[13px] text-[var(--martian-ink-muted,var(--text-secondary))] hover:text-[var(--martian-violet,var(--text-primary))] py-1.5"
              >
                {p}
              </button>
            ))}
          </div>
        ) : (
          thread.map((m, i) => (
            <div key={i} data-testid={`talk-to-martian-message-${m.role}`}>
              {m.role === 'user' ? (
                <p className="text-[12px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{m.text}</p>
              ) : (
                <div className="space-y-1">
                  {/* Milestone 36 (Section 3) -- JUDGMENT first, always the
                       lead line: the structured `judgment` field when one
                       exists (already the short, direct sentence
                       talk_to_martian.py's own structured-fields builder
                       derived from the SAME real evidence as `text`), or
                       the plain reply text for every response this
                       milestone didn't restructure (a HOLD recommendation,
                       a plan/change-plan reply, the generic fallback --
                       Section 2: "Do NOT require every response to use
                       every section"). */}
                  <p className="text-[11.5px] text-[var(--martian-ink-muted,var(--text-tertiary))] italic leading-relaxed">
                    {m.judgment || m.text}
                  </p>

                  {/* HONEST LIMITATION -- Section 10: visually distinct
                       from a normal answer, and labeled by its real kind
                       (capability unavailable / data unavailable /
                       insufficient data / unsupported), never a single
                       generic "I don't know." */}
                  {m.limitation && (
                    <div data-testid="talk-to-martian-limitation" className="pl-2 border-l-2 border-[var(--martian-rule-strong,var(--border))]">
                      <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]">
                        {LIMITATION_KIND_LABEL[m.limitation.kind] || 'Limitation'}
                      </p>
                      {m.limitation.text !== m.judgment && m.limitation.text !== m.text && (
                        <p className="text-[11px] text-[var(--martian-ink-muted,var(--text-tertiary))] leading-relaxed">{m.limitation.text}</p>
                      )}
                    </div>
                  )}

                  {/* EVIDENCE & REASONING -- Section 5: reuses the SAME
                       collapsed-by-default disclosure pattern established
                       in SahibBriefingPage.js (M18/M19), never expanded by
                       default, never a raw database object. */}
                  {m.evidence && (m.evidence.items || []).length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setExpandedEvidence((prev) => ({ ...prev, [i]: !prev[i] }))}
                        data-testid="talk-to-martian-evidence-toggle"
                        aria-expanded={!!expandedEvidence[i]}
                        className="flex items-center gap-1 text-[10.5px] font-medium text-[var(--martian-violet,var(--accent))]"
                      >
                        Evidence &amp; reasoning
                        <CaretDown size={10} className={`transition-transform motion-reduce:transition-none ${expandedEvidence[i] ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedEvidence[i] && (
                        <dl data-testid="talk-to-martian-evidence" className="grid grid-cols-2 gap-x-3 gap-y-1 pt-0.5">
                          {m.evidence.items.map((it, idx) => (
                            <div key={idx} className="flex items-baseline justify-between gap-2 text-[10px] col-span-2">
                              <dt className="text-[var(--martian-ink-muted,var(--text-tertiary))] shrink-0">{EVIDENCE_LABELS[it.title] || it.title}</dt>
                              <dd className="font-mono text-[var(--martian-ink,var(--text-primary))] text-right">{formatEvidenceValue(it.title, it.summary)}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </>
                  )}

                  {/* RECOMMENDATION -- Section 6: visually distinguishable
                       from evidence, a small violet rule (the ONE accent,
                       used sparingly per the existing visual system), no
                       giant colored card. */}
                  {m.recommendation && (
                    <div data-testid="talk-to-martian-recommendation" className="pl-2 border-l-2 border-[var(--martian-violet,var(--accent))]">
                      <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--martian-violet,var(--accent))]">Next move</p>
                      <p className="text-[11.5px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed">{m.recommendation.text}</p>
                    </div>
                  )}

                  {/* PREPARED WORK -- Section 7: a compact pointer to
                       real, already-existing prepared work, never a
                       duplicate card, never a silent execution. Reuses
                       the same "Review" action language the real
                       prepared-work queue itself uses. */}
                  {m.preparedWork && (
                    <div data-testid="talk-to-martian-prepared-work" className="flex items-center justify-between gap-2 border-t border-[var(--martian-rule,var(--border-light))] pt-1.5 mt-0.5">
                      <span className="text-[11px] text-[var(--martian-ink,var(--text-primary))] leading-snug">
                        {m.preparedWork.title}
                        {m.preparedWork.count > 1 && (
                          <span className="text-[var(--martian-ink-muted,var(--text-tertiary))]"> · {m.preparedWork.count} opportunities</span>
                        )}
                      </span>
                      <span className="text-[10.5px] font-semibold text-[var(--martian-violet,var(--accent))] shrink-0">Review →</span>
                    </div>
                  )}

                  {/* Milestone 30 -- a real, honest "not connected" reply gets
                       a small inline CTA, never an intrusive auto-opened
                       modal (Section 15: the connection flow should feel like
                       a natural extension of Martian, not a settings form
                       dumped into chat). Phase 0 (P0.9): it routes to
                       Connections -- credentials are never entered in Talk. */}
                  {m.connectCta && (
                    <button
                      type="button"
                      onClick={goToConnections}
                      data-testid="talk-to-martian-connect-cta"
                      className="mt-1 text-[11.5px] font-semibold text-[var(--martian-violet,var(--accent))] hover:underline"
                    >
                      Set up ChatGPT Ads in Connections
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}

        {/* Milestone 23 -- the campaign plan as a decision surface, not a
             generic SaaS form (Section 11). Real fields only, real
             status, no visual treatment beyond this panel's own existing
             language (violet accent, mono labels, hairline rules). */}
        {plan && (
          <div className="border-t-2 border-[var(--martian-rule-strong,var(--border-light))] pt-3 mt-2 space-y-2.5" data-testid="chatgpt-ads-plan-card">
            <div className="flex items-center gap-1.5">
              <Rocket size={13} weight="fill" className="text-[var(--martian-violet,var(--accent))]" />
              <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="chatgpt-ads-plan-status">
                {planIsReady ? 'Ready to launch' : plan.status === 'executed' ? 'Launched' : plan.status === 'execution_failed' ? "Couldn't launch" : plan.status === 'cancelled' ? 'Cancelled' : 'Building the plan'}
              </p>
            </div>

            <div className="text-[12px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed space-y-1">
              <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">ChatGPT Ads · </span>{plan.fields.campaign_name || '—'}</p>
              {plan.fields.goal && <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Goal: </span>{plan.fields.goal}</p>}
              {plan.fields.country_codes && <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Market: </span>{plan.fields.country_codes.join(', ')}</p>}
              {/* Section 10: LIFETIME budget only, never "daily" -- and
                   never a guessed currency. */}
              {plan.fields.lifetime_budget_amount && (
                <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Lifetime budget: </span>{plan.fields.lifetime_budget_amount}{plan.execution_receipt?.currency_code ? ` ${plan.execution_receipt.currency_code}` : ''}</p>
              )}
              {plan.fields.end_date && <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">End date: </span>{plan.fields.end_date}</p>}
              {plan.fields.headline && <p className="pt-1"><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Headline: </span>{plan.fields.headline}</p>}
              {plan.fields.body && <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Body: </span>{plan.fields.body}</p>}
              {plan.fields.destination_url && <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Destination: </span>{plan.fields.destination_url}</p>}
              {plan.rationale && <p className="pt-1 italic text-[var(--martian-ink-muted,var(--text-secondary))]">Martian recommends this because {plan.rationale}</p>}
            </div>

            {plan.execution_receipt && (
              <p className="text-[10.5px] font-mono text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="chatgpt-ads-plan-receipt">
                Real campaign id: {plan.execution_receipt.campaign_id}
              </p>
            )}

            {planIsReady && (
              <div className="flex items-center gap-2 pt-1">
                {/* Milestone 30 -- when we already know (via the real
                     GET /vault/keys check above) that ChatGPT Ads isn't
                     connected, offer the real connection step in place of
                     a Launch attempt that would only fail -- the same
                     honest information, surfaced before the click instead
                     of after it. `chatgptAdsConnected === false` only
                     (never the initial `null`), so this never flashes
                     while the real check is still in flight. */}
                {chatgptAdsConnected === false ? (
                  <Button size="sm" onClick={goToConnections} data-testid="chatgpt-ads-connect-button">
                    Set up in Connections
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleApprove} disabled={actingOnPlan} data-testid="chatgpt-ads-launch-button">
                    {actingOnPlan ? 'Launching…' : 'Launch campaign'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={handleCancelPlan} disabled={actingOnPlan} data-testid="chatgpt-ads-cancel-button">
                  Edit plan
                </Button>
              </div>
            )}
            {!planIsReady && !planIsTerminal && (
              <Button size="sm" variant="ghost" onClick={handleCancelPlan} disabled={actingOnPlan} data-testid="chatgpt-ads-cancel-button">
                Cancel
              </Button>
            )}
          </div>
        )}

        {/* Milestone 28 -- the SAME decision-surface visual pattern as
             the plan card above (Section 16: reuse the existing UI
             primitive, never build a second approval system), for a
             management/recommendation change awaiting approval. */}
        {changePlan && (
          <div className="border-t-2 border-[var(--martian-rule-strong,var(--border-light))] pt-3 mt-2 space-y-2.5" data-testid="chatgpt-ads-change-plan-card">
            <div className="flex items-center gap-1.5">
              <Rocket size={13} weight="fill" className="text-[var(--martian-violet,var(--accent))]" />
              <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="chatgpt-ads-change-plan-status">
                {changePlanIsReady ? 'Ready to review' : changePlan.status === 'executed' ? 'Done' : changePlan.status === 'execution_failed' ? "Couldn't complete" : changePlan.status === 'cancelled' ? 'Cancelled' : 'Preparing'}
              </p>
            </div>

            <div className="text-[12px] text-[var(--martian-ink,var(--text-primary))] leading-relaxed space-y-1">
              <p><span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">ChatGPT Ads · </span>{changePlan.campaign_name || '—'}</p>
              {changePlan.action === 'update_budget' ? (
                <p>
                  <span className="text-[var(--martian-ink-muted,var(--text-tertiary))]">Lifetime budget: </span>
                  {formatMoney(changePlan.current_state?.lifetime_budget_amount, changePlan.current_state?.currency_code)}
                  {' → '}
                  {formatMoney(changePlan.proposed_state?.lifetime_budget_amount, changePlan.current_state?.currency_code)}
                </p>
              ) : (
                <p>{changePlan.action === 'pause' ? 'Pause this campaign' : changePlan.action === 'resume' ? 'Resume this campaign' : changePlan.action}</p>
              )}
              {changePlan.rationale && <p className="pt-1 italic text-[var(--martian-ink-muted,var(--text-secondary))]">Martian recommends this because {changePlan.rationale}</p>}
            </div>

            {changePlan.execution_receipt && (
              <p className="text-[10.5px] font-mono text-[var(--martian-ink-muted,var(--text-tertiary))]" data-testid="chatgpt-ads-change-plan-receipt">
                {describeChangeExecuted(changePlan)}
              </p>
            )}

            {changePlanIsReady && (
              <div className="flex items-center gap-2 pt-1">
                {chatgptAdsConnected === false ? (
                  <Button size="sm" onClick={goToConnections} data-testid="chatgpt-ads-connect-button">
                    Set up in Connections
                  </Button>
                ) : (
                  <Button size="sm" onClick={handleApproveChange} disabled={actingOnChangePlan} data-testid="chatgpt-ads-approve-change-button">
                    {actingOnChangePlan ? 'Working…' : 'Approve change'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={handleCancelChange} disabled={actingOnChangePlan} data-testid="chatgpt-ads-cancel-change-button">
                  Cancel
                </Button>
              </div>
            )}
            {!changePlanIsReady && !changePlanIsTerminal && (
              <Button size="sm" variant="ghost" onClick={handleCancelChange} disabled={actingOnChangePlan} data-testid="chatgpt-ads-cancel-change-button">
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex items-center gap-1.5 pt-3 border-t-2 border-[var(--martian-rule-strong,var(--border-light))]">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask Martian…"
          data-testid="talk-to-martian-input"
          disabled={sending}
          className="flex-1 min-w-0 text-[12.5px] bg-transparent outline-none text-[var(--martian-ink,var(--text-primary))] placeholder:text-[var(--text-placeholder)]"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="talk-to-martian-send"
          aria-label="Send"
          className="text-[var(--martian-violet,var(--accent))] disabled:text-[var(--text-disabled)] shrink-0"
        >
          <PaperPlaneRight size={15} weight="fill" />
        </button>
      </form>

    </div>
  );
}
